import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { FiHeart, FiShoppingCart, FiLayers, FiTruck, FiBriefcase, FiBookOpen } from "react-icons/fi";

const IndustriesWeServe = () => {
  const { t } = useTranslation();

  const industries = [
    {
      icon: FiHeart,
      titleKey: "ind_health_title",
      defaultTitle: "Healthcare",
      descKey: "ind_health_desc",
      defaultDesc: "Custom patient portals, EHR integration, telehealth solutions, and HIPAA-compliant data pipelines.",
    },
    {
      icon: FiShoppingCart,
      titleKey: "ind_retail_title",
      defaultTitle: "Retail & E-Commerce",
      descKey: "ind_retail_desc",
      defaultDesc: "Sleek store designs, secure checkout systems, multi-vendor platforms, and inventory syncing.",
    },
    {
      icon: FiLayers,
      titleKey: "ind_mfg_title",
      defaultTitle: "Manufacturing",
      descKey: "ind_mfg_desc",
      defaultDesc: "IoT automation monitoring, predictive maintenance tracking, and custom ERP integration.",
    },
    {
      icon: FiTruck,
      titleKey: "ind_log_title",
      defaultTitle: "Logistics & Supply Chain",
      descKey: "ind_log_desc",
      defaultDesc: "Real-time fleet tracking maps, dispatch systems, warehouse coordination, and route optimizing.",
    },
    {
      icon: FiBriefcase,
      titleKey: "ind_ent_title",
      defaultTitle: "Enterprise Systems",
      descKey: "ind_ent_desc",
      defaultDesc: "Cross-department database syncing, legacy codebase migrations, custom dashboards, and SAP consultancies.",
    },
    {
      icon: FiBookOpen,
      titleKey: "ind_edu_title",
      defaultTitle: "Education & E-Learning",
      descKey: "ind_edu_desc",
      defaultDesc: "Interactive learning systems, online test modules, virtual classroom structures, and school ERPs.",
    },
  ];

  return (
    <section className="py-20 md:py-28 bg-slate-50 dark:bg-[#0c0c0e] transition-colors duration-500 overflow-hidden relative">
      {/* Glow background */}
      <div className="absolute top-1/2 left-0 w-80 h-80 rounded-full bg-blue-500/5 dark:bg-purple-500/5 blur-3xl" />

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Title */}
        <div className="text-center mb-16">
          <span className="text-xs font-bold text-blue-600 dark:text-purple-400 uppercase tracking-widest">
            {t('industriesLabel', 'Sectors We Power')}
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-slate-900 dark:text-white mt-2 tracking-tight">
            {t('industriesTitle', 'Industries We Serve')}
          </h2>
          <div className="w-20 h-1 bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-purple-500 dark:to-pink-500 mx-auto mt-4 rounded-full" />
        </div>

        {/* Industries Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {industries.map((ind, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.1 }}
              transition={{ delay: index * 0.08, duration: 0.5 }}
              whileHover={{ 
                scale: 1.02,
                y: -6,
                transition: { duration: 0.2 }
              }}
              className="group relative p-6 sm:p-8 rounded-3xl bg-slate-50/50 hover:bg-white dark:bg-[#111111]/30 dark:hover:bg-[#111111] border border-slate-200/50 dark:border-neutral-800/40 hover:border-blue-500/30 dark:hover:border-purple-500/30 shadow-sm hover:shadow-2xl hover:shadow-blue-500/5 dark:hover:shadow-purple-500/5 overflow-hidden transition-all duration-300"
            >
              {/* Highlight Background on hover */}
              <div className="absolute -inset-px bg-gradient-to-br from-blue-500/5 to-purple-500/5 dark:from-purple-500/5 dark:to-pink-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-3xl" />

              {/* Icon */}
              <div className="relative z-10 w-12 h-12 flex items-center justify-center rounded-2xl bg-blue-50 dark:bg-neutral-900 text-blue-600 dark:text-purple-400 group-hover:bg-gradient-to-r group-hover:from-blue-600 group-hover:to-indigo-600 dark:group-hover:from-purple-500 dark:group-hover:to-pink-500 group-hover:text-white dark:group-hover:text-white transition-all duration-300 mb-6">
                <ind.icon className="w-5 h-5" />
              </div>

              {/* Title */}
              <h3 className="relative z-10 text-lg font-extrabold text-slate-900 dark:text-white mb-3">
                {t(ind.titleKey, ind.defaultTitle)}
              </h3>

              {/* Description */}
              <p className="relative z-10 text-sm text-slate-500 dark:text-neutral-400 leading-relaxed">
                {t(ind.descKey, ind.defaultDesc)}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default IndustriesWeServe;
